import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { Message, SuggestedUrl, ApiMessage } from '../types';
import { fetchToken, streamChat, clearToken } from '../api';

interface ChatWidgetProps {
  apiBaseUrl: string;
  /** uHaven's privacy statement. Empty until that page exists — see main.tsx. */
  privacyUrl?: string;
}

// Generate unique ID
const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Quick exit URL - neutral website
const QUICK_EXIT_URL = 'https://www.google.com';

// Icons as SVG components
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
  </svg>
);

// AU phone numbers (000, 13/1300/1800 numbers, landlines, mobiles) and service
// URLs/bare domains (e.g. cawls.org.au) as they appear in replies.
const PHONE_RE = /\b(?:000|1[38]00\s?\d{3}\s?\d{3}|13\s?\d{2}\s?\d{2}|13\d\s?\d{3}|0\d\s?\d{4}\s?\d{4}|04\d{2}\s?\d{3}\s?\d{3})\b/;
const URL_RE = /\bhttps?:\/\/[^\s)]+|\b(?:[a-z0-9-]+\.)+(?:com|org|net|gov|edu)(?:\.au)?(?:\/[^\s)]*)?\b/i;
const LINK_RE = new RegExp(`(${URL_RE.source})|(${PHONE_RE.source})`, 'gi');

// Turn phone numbers into click-to-call (tel:) links and URLs/domains into hyperlinks.
// Inputs are plain strings, so React escapes the visible text (XSS-safe).
function linkify(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const token = m[0];
    if (m[1] !== undefined) {
      // Don't let trailing sentence punctuation get pulled into the URL.
      const trailing = token.match(/[.,;:!?]+$/)?.[0] ?? '';
      const url = trailing ? token.slice(0, -trailing.length) : token;
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      nodes.push(
        <a key={`${keyPrefix}l${i++}`} href={href} target="_blank" rel="noopener noreferrer">{url}</a>
      );
      if (trailing) nodes.push(trailing);
    } else {
      nodes.push(<a key={`${keyPrefix}l${i++}`} href={`tel:${token.replace(/\s+/g, '')}`}>{token}</a>);
    }
    last = LINK_RE.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// Render the inline formatting the model emits: emphasis (***bold italic***, **bold**,
// *italic*), click-to-call phone numbers, and service hyperlinks. Inline content only —
// renderMessageBody below handles line breaks and bullets.
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re = /\*\*\*([^*]+)\*\*\*|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(...linkify(text.slice(last, m.index), `t${i}`));
    const k = `e${i++}`;
    if (m[1] !== undefined) nodes.push(<strong key={k}><em>{linkify(m[1], k)}</em></strong>);
    else if (m[2] !== undefined) nodes.push(<strong key={k}>{linkify(m[2], k)}</strong>);
    else if (m[3] !== undefined) nodes.push(<em key={k}>{linkify(m[3], k)}</em>);
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(...linkify(text.slice(last), `t${i}`));
  return nodes;
}

// Render a reply's block structure: paragraphs and "- " bullet lists.
//
// The backend used to flatten every reply to a single line, so this component only ever
// had to deal with inline content. It no longer does — the client asked for dot points
// in longer answers — so consecutive "- " lines are collected into a <ul> and everything
// else becomes a paragraph. The backend normalises every list marker to "- " first, so
// that one prefix is all there is to look for.
function renderMessageBody(text: string): React.ReactNode {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length === 0) return;
    const items = bullets;
    bullets = [];
    blocks.push(
      <ul key={`ul${blocks.length}`} className="uhaven-chat-message-list">
        {items.map((item, n) => (
          <li key={n}>{renderInlineMarkdown(item)}</li>
        ))}
      </ul>
    );
  };

  for (const line of lines) {
    const bullet = /^-\s+(.*)$/.exec(line);
    if (bullet) {
      bullets.push(bullet[1]);
      continue;
    }
    flushBullets();
    if (line.trim() === '') continue;
    blocks.push(
      <p key={`p${blocks.length}`} className="uhaven-chat-message-para">
        {renderInlineMarkdown(line)}
      </p>
    );
  }
  flushBullets();

  return blocks;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ apiBaseUrl, privacyUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Anonymous id that groups the turns of this chat session for recording.
  // Not tied to identity; reset whenever the conversation is cleared.
  const conversationIdRef = useRef<string>(crypto.randomUUID());
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Keep the cursor in the message box. The input stays enabled while a reply is
  // generating (we never disable it), so focus is never dropped and the user can keep
  // typing — like MS Teams — without clicking back in. We still focus on open, and
  // re-focus when loading ends to cover the case where they clicked the Send button
  // (which moves focus to the button) rather than pressing Enter.
  //
  // Clear is the same case and was missed: it is a header button, so clicking it takes
  // focus out of the input, and neither isOpen nor isLoading changes — so this effect
  // does not re-run. handleClearChat hands focus back itself.
  useEffect(() => {
    if (isOpen && !isLoading) {
      inputRef.current?.focus();
    }
  }, [isOpen, isLoading]);
  
  // Handle quick exit
  const handleQuickExit = useCallback(() => {
    // Clear chat history
    setMessages([]);
    // Redirect to neutral page
    window.location.replace(QUICK_EXIT_URL);
  }, []);
  
  // Handle clear chat
  const handleClearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    clearToken();
    // Start a fresh anonymous conversation
    conversationIdRef.current = crypto.randomUUID();
    // Clicking Clear moved focus to the header button. Put the cursor back in the
    // message box so the person can just keep typing.
    inputRef.current?.focus();
  }, []);
  
  // Handle message submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;
    
    // Add user message
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: trimmedInput,
    };
    
    // Add placeholder assistant message
    const assistantMessage: Message = {
      id: generateId(),
      role: 'assistant',
      content: '',
      isStreaming: true,
    };
    
    // Build the conversation history to send (prior turns + this new message), so
    // the model has context. Drop error bubbles and any empty content.
    const outgoing: ApiMessage[] = [
      ...messages
        .filter(m => !m.isError && m.content.trim().length > 0)
        .map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content: trimmedInput },
    ];

    setMessages(prev => [...prev, userMessage, assistantMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      // Get token
      const { token } = await fetchToken(apiBaseUrl);
      
      let finalContent = '';
      let disclaimerContent = '';
      let suggestedUrlsContent: SuggestedUrl[] = [];
      let hadError = false;
      
      await streamChat(
        apiBaseUrl,
        token,
        outgoing,
        conversationIdRef.current,
        // onFinal - the full cleaned reply from the backend
        (final) => {
          finalContent = final;
        },
        // onDisclaimer
        (disclaimer) => {
          disclaimerContent = disclaimer;
        },
        // onSuggestedUrls
        (urls) => {
          suggestedUrlsContent = urls;
        },
        // onError
        (errorMsg) => {
          hadError = true;
          setMessages(prev => 
            prev.map(msg => 
              msg.id === assistantMessage.id 
                ? { 
                    ...msg, 
                    content: errorMsg,
                    isStreaming: false,
                    isError: true,
                    disclaimer: disclaimerContent || "uHaven is not an emergency service. If you are in immediate danger, call 000. For confidential support call 1800 RESPECT (1800 737 732).",
                  }
                : msg
            )
          );
        },
        // onDone
        () => {
          if (!hadError) {
            const content = finalContent;
            setMessages(prev => 
              prev.map(msg => 
                msg.id === assistantMessage.id 
                  ? { 
                      ...msg, 
                      content,
                      isStreaming: false,
                      disclaimer: disclaimerContent,
                      suggestedUrls: suggestedUrlsContent,
                    }
                  : msg
              )
            );
          }
          setIsLoading(false);
        }
      );
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setMessages(prev => 
        prev.map(msg => 
          msg.id === assistantMessage.id 
            ? { 
                ...msg, 
                content: errorMessage,
                isStreaming: false,
                isError: true,
                disclaimer: "uHaven is not an emergency service. If you are in immediate danger, call 000. For confidential support call 1800 RESPECT (1800 737 732).",
              }
            : msg
        )
      );
      setIsLoading(false);
    }
  }, [apiBaseUrl, input, isLoading, messages]);
  
  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Escape to close
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);
  
  return (
    <div className="uhaven-chat-widget" onKeyDown={handleKeyDown}>
      {/* Chat window */}
      {isOpen && (
        <div className="uhaven-chat-window" role="dialog" aria-label="Chat with uHaven">
          {/* Header */}
          <div className="uhaven-chat-header">
            <div className="uhaven-chat-header-title">
              <h2>Ask uHaven</h2>
              {/* All three standing disclaimers live here, in the purple bar, so they
                  are on screen the whole time rather than scrolling away with the
                  welcome card. The personal-information line comes first because it is
                  the one that has to arrive before the person starts typing. */}
              <p className="uhaven-chat-header-disclaimer">
                <strong>Please don&rsquo;t share personal information.</strong>{' '}
                {renderInlineMarkdown('Not an emergency service. If in danger, call 000. For support: 1800 RESPECT (1800 737 732)')}
              </p>
            </div>
            <div className="uhaven-chat-header-actions">
              <button 
                className="uhaven-chat-header-btn"
                onClick={handleClearChat}
                aria-label="Clear chat"
              >
                Clear
              </button>
              <button 
                className="uhaven-chat-header-btn uhaven-chat-quick-exit"
                onClick={handleQuickExit}
                aria-label="Quick exit - leave this page immediately"
                title="Leave this page immediately"
              >
                Quick Exit
              </button>
            </div>
          </div>
          
          {/* Error banner */}
          {error && (
            <div className="uhaven-chat-error-banner">
              {error}
            </div>
          )}
          
          {/* Messages */}
          <div className="uhaven-chat-messages">
            {messages.length === 0 && (
              <div className="uhaven-chat-welcome">
                <h3>Welcome to Ask uHaven</h3>
                <p>
                  I can help provide general information about healthy and unhealthy
                  relationships, or connect you to local services for help.
                </p>
                {/* What this tool is and is not. The "don't share personal information"
                    warning used to sit here as a paragraph of its own; it now lives in
                    the header bar with the other disclaimers, where it stays on screen
                    instead of scrolling away after the first message. If someone does
                    share a personal detail, the bot explains it properly at that point —
                    see the PERSONAL INFORMATION section of the system prompt. */}
                <div className="uhaven-chat-welcome-notice">
                  <p>
                    I give general information and can point you to a service &mdash; I
                    can&rsquo;t give legal advice or counselling, and I&rsquo;m not an
                    emergency service. If you or someone else is in immediate danger, call
                    000.
                    {privacyUrl && (
                      <>
                        {' '}
                        <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
                          How we handle your information
                        </a>
                        .
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}
            
            {messages.map((message) => (
              <div 
                key={message.id}
                className={`uhaven-chat-message uhaven-chat-message-${message.role}${message.isError ? ' uhaven-chat-message-error' : ''}`}
              >
                <div className="uhaven-chat-message-content">
                  {renderMessageBody(message.content)}
                  {message.isStreaming && <span className="uhaven-chat-streaming" />}
                </div>
                {message.suggestedUrls && message.suggestedUrls.length > 0 && (
                  <div className="uhaven-chat-suggested-urls">
                    <span className="uhaven-chat-suggested-label">Learn more:</span>
                    {message.suggestedUrls.map((item) => (
                      <a 
                        key={item.key}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="uhaven-chat-suggested-link"
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                )}
                {message.disclaimer && (
                  <div className="uhaven-chat-disclaimer">
                    {renderInlineMarkdown(message.disclaimer)}
                  </div>
                )}
              </div>
            ))}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input area */}
          <div className="uhaven-chat-input-area">
            <form className="uhaven-chat-input-form" onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                type="text"
                className="uhaven-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                maxLength={1000}
                /* Quick Exit clears the screen but cannot clear browser autofill, so a
                   phrase typed here could resurface in the dropdown on a shared or
                   monitored device. Keep every autofill mechanism off. */
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                name="uhaven-chat-message"
                aria-label="Message input"
              />
              <button
                type="submit"
                className="uhaven-chat-send-btn"
                disabled={isLoading || !input.trim()}
                aria-label="Send message"
              >
                <SendIcon />
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Toggle button */}
      <button
        className="uhaven-chat-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        aria-expanded={isOpen}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </button>
    </div>
  );
};
